import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiSearch, FiUsers } from 'react-icons/fi';
import { toast } from 'react-toastify';
import customerService from '../services/customerService';
import Button from '../components/Button';
import './Customers.css';
import '../assets/styles/ListTable.css';

const LIMIT_OPTIONS = [10, 20, 50, 100];

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [customerType, setCustomerType] = useState('all');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [limitOption, setLimitOption] = useState('10');
  const [customLimit, setCustomLimit] = useState(25);
  const searchDebounce = useRef(null);

  const effectiveLimit = useMemo(() => {
    if (limitOption === 'custom') {
      const n = parseInt(customLimit, 10);
      return isNaN(n) ? 10 : Math.min(100, Math.max(1, n));
    }
    const n = parseInt(limitOption, 10);
    return isNaN(n) ? 10 : n;
  }, [limitOption, customLimit]);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setAppliedSearch(searchTerm), 400);
    return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch, customerType, includeInactive, effectiveLimit]);

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, appliedSearch, customerType, includeInactive, effectiveLimit]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: effectiveLimit };
      if (appliedSearch) params.search = appliedSearch;
      if (customerType !== 'all') params.customerType = customerType;
      if (includeInactive) params.includeInactive = 'true';
      const response = await customerService.getAll(params);
      setCustomers(response.data || []);
      setTotal(response.total ?? 0);
      setPages(response.pages ?? 1);
    } catch (error) {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await customerService.delete(id);
        toast.success('Customer deleted successfully');
        fetchCustomers();
      } catch (error) {
        const message = error.response?.data?.message || 'Failed to delete customer';
        toast.error(message);
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Customers</h2>
          <p className="page-subtitle">Manage your HVAC customers and contacts</p>
        </div>
        <Link to="/customers/new">
          <Button variant="primary" icon={<FiPlus />}>
            Add Customer
          </Button>
        </Link>
      </div>

      <div className="page-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <select
          value={customerType}
          onChange={(e) => setCustomerType(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Types</option>
          <option value="residential">Residential</option>
          <option value="commercial">Commercial</option>
          <option value="industrial">Industrial</option>
          <option value="other">Other</option>
        </select>

        <label className="customers-toggle">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          <span>Show inactive</span>
        </label>
      </div>

      {customers.length === 0 ? (
        <div className="empty-state">
          <FiUsers className="empty-icon" />
          <h3>No Customers Found</h3>
          <p>Get started by adding your first customer</p>
          <Link to="/customers/new">
            <Button variant="primary" icon={<FiPlus />}>
              Add Customer
            </Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th>Tags</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer._id}>
                    <td>
                      <strong>{customer.name}</strong>
                    </td>
                    <td>{customer.phone}</td>
                    <td>{customer.email || '—'}</td>
                    <td style={{ textTransform: 'capitalize' }}>
                      {customer.customerType || '—'}
                    </td>
                    <td>
                      {(customer.tags || []).length === 0
                        ? '—'
                        : (customer.tags || []).join(', ')}
                    </td>
                    <td>
                      <span className={customer.isActive ? 'badge badge-success' : 'badge badge-gray'}>
                        {customer.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="cell-actions">
                        <Link to={`/customers/${customer._id}`} className="action-btn action-btn-view">
                          <FiEye /> View
                        </Link>
                        <Link
                          to={`/customers/${customer._id}/edit`}
                          className="action-btn action-btn-edit"
                        >
                          <FiEdit /> Edit
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDelete(customer._id)}
                          className="action-btn action-btn-delete"
                        >
                          <FiTrash2 /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(pages > 1 || total > customers.length || total > 0) && (
            <div className="pagination-bar">
              <span className="pagination-info">
                Showing {(currentPage - 1) * effectiveLimit + 1}–
                {Math.min(currentPage * effectiveLimit, total)} of {total}
              </span>
              <div className="pagination-limit">
                <label className="pagination-limit-label">Per page</label>
                <select
                  value={limitOption}
                  onChange={(e) => setLimitOption(e.target.value)}
                  className="pagination-limit-select"
                >
                  {LIMIT_OPTIONS.map((n) => (
                    <option key={n} value={String(n)}>
                      {n}
                    </option>
                  ))}
                  <option value="custom">Custom</option>
                </select>
                {limitOption === 'custom' && (
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={customLimit}
                    onChange={(e) =>
                      setCustomLimit(parseInt(e.target.value, 10) || 10)
                    }
                    className="pagination-limit-input"
                  />
                )}
              </div>
              <div className="pagination-controls">
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="pagination-info" style={{ padding: '0 0.75rem' }}>
                  Page {currentPage} of {pages}
                </span>
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.min(pages, p + 1))}
                  disabled={currentPage === pages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Customers;

